// Copyright 2026 Zibby Lab. Apache-2.0.
//
// Shared log-line parsing for the container (apps) + workflow log viewers.
// Both viewers render the same `.app-logs-*` markup via <LogLine>; this is
// the classification half so highlighting (severity stripe + level badge +
// dimmed body) is identical across them.
//
//   parseLogPayload(text, opts) — classify a payload (NO leading timestamp)
//                                 into { level, msg, body, severity }.
//   parseLogLine(raw)           — split a "TS␠␠payload" line into
//                                 { ts, level, msg, body, severity }.

// Severity precedence:
//   1. explicit JSON `level` (ERROR/FATAL → error, WARN/WARNING → warn)
//   2. keyword sniff — npm's "warn deprecated" / "ERESOLVE" / "error:" all
//      arrive at INFO from the bash-tool wrapper but a human reads them as
//      warn/error; a cheap regex keeps the visual signal honest.
//   3. info
function classifySeverity(level, text) {
  if (level === 'ERROR' || level === 'FATAL') return 'error';
  if (level === 'WARN' || level === 'WARNING') return 'warn';
  const t = typeof text === 'string' ? text : '';
  if (/\b(error|failed|fatal|eresolve)\b/i.test(t)) return 'error';
  if (/\b(warn|warning|deprecated)\b/i.test(t)) return 'warn';
  return 'info';
}

/**
 * @param {string} text   the payload (already stripped of any leading ts)
 * @param {{ sniffPlain?: boolean }} [opts]
 *   sniffPlain — also keyword-sniff NON-JSON plain text. Apps logs leave this
 *   off (their payloads are JSON-wrapped by the bash tool, so plain lines are
 *   rare and stay info); workflow logs turn it on since their messages are
 *   raw container output where "Warning"/"error" appears in plain text.
 * @returns {{ level: string|null, msg: string|null, body: string, severity: 'info'|'warn'|'error' }}
 */
export function parseLogPayload(text, { sniffPlain = false } = {}) {
  const raw = typeof text === 'string' ? text : '';
  const trimmed = raw.trim();

  const plain = () => ({
    level: null,
    msg: null,
    body: raw,
    severity: sniffPlain ? classifySeverity(null, raw) : 'info',
  });

  if (!trimmed.startsWith('{')) return plain();

  let obj;
  try { obj = JSON.parse(trimmed); } catch { return plain(); }

  const level = (obj.level || '').toUpperCase() || null;
  const msg = obj.msg || null;

  // Render the "interesting" part. For bash-wrapped script output
  // (msg=bootstrap-script, stream=stderr|stdout, line=…) the `line` field IS
  // the user-facing payload; show it instead of the JSON bookkeeping.
  // Otherwise condense the remaining fields.
  let body = '';
  if (typeof obj.line === 'string') {
    body = obj.line;
    if (obj.stream && obj.stream !== 'stdout') body = `[${obj.stream}] ${body}`;
  } else {
    const ignored = new Set(['time', 'level', 'msg']);
    const fields = Object.entries(obj).filter(([k]) => !ignored.has(k));
    if (fields.length > 0) {
      body = fields.map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`).join(' ');
    }
  }

  return { level, msg, body, severity: classifySeverity(level, body) };
}

/**
 * Apps-log line: "<timestamp>␠␠<json-or-plain>". Splits on the first
 * double-space. Preserves the original apps behaviour exactly — plain
 * (non-JSON) payloads are NOT keyword-sniffed (sniffPlain stays off).
 * @returns {{ ts: string|null, level: string|null, msg: string|null, body: string, severity: string }}
 */
export function parseLogLine(raw) {
  if (typeof raw !== 'string' || raw.length < 1) {
    return { ts: null, level: null, msg: null, body: raw, severity: 'info' };
  }
  const idx = raw.indexOf('  ');
  if (idx < 0) {
    return { ts: null, level: null, msg: null, body: raw, severity: 'info' };
  }
  const ts = raw.slice(0, idx);
  const rest = raw.slice(idx + 2).trim();
  return { ts, ...parseLogPayload(rest) };
}
